import React, { useState, useEffect } from 'react';
import { MagnifyingGlassIcon, UserPlusIcon, ClockIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase-auth';
import { useAuth } from '../../contexts/AuthContextLazy';
import { formatDistanceToNow } from 'date-fns';

interface Client {
  id: string;
  name: string;
  primary_email: string;
  primary_phone?: string;
  last_needs_analysis?: {
    id: string;
    created_at: string;
    status: string;
  };
}

interface ClientSelectorProps {
  onSelectClient: (client: Client | null) => void;
  documentType: 'needs_analysis' | 'reason_why';
}

export default function ClientSelector({ onSelectClient, documentType }: ClientSelectorProps) {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [clients, setClients] = useState<Client[]>([]);
  const [recentClients, setRecentClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecentClients();
  }, [user]);

  useEffect(() => {
    if (searchQuery.length >= 2) {
      searchClients();
    }
  }, [searchQuery]);

  const fetchRecentClients = async () => {
    try {
      // Get clients with recent documents - show all clients
      const { data, error } = await supabase
        .from('parties')
        .select(`
          id,
          name,
          primary_email,
          primary_phone,
          compliance_documents!compliance_documents_client_id_fkey(
            id,
            document_type,
            status,
            created_at
          )
        `)
        .order('updated_at', { ascending: false })
        .limit(10);

      if (error) throw error;

      // Process clients to include last needs analysis info
      const processedClients = (data || []).map(client => {
        const needsAnalyses = client.compliance_documents?.filter(
          (doc: any) => doc.document_type === 'needs_analysis'
        ) || [];
        
        const lastNA = needsAnalyses.sort((a: any, b: any) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        )[0];

        return {
          id: client.id,
          name: client.name,
          primary_email: client.primary_email,
          primary_phone: client.primary_phone,
          last_needs_analysis: lastNA ? {
            id: lastNA.id,
            created_at: lastNA.created_at,
            status: lastNA.status
          } : undefined
        };
      });

      setRecentClients(processedClients);
    } catch (error) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Error fetching recent clients:', error);
        }

      }
    } finally {
      setLoading(false);
    }
  };

  const searchClients = async () => {
    try {
      const { data, error } = await supabase
        .from('parties')
        .select(`
          id,
          name,
          primary_email,
          primary_phone,
          compliance_documents!compliance_documents_client_id_fkey(
            id,
            document_type,
            status,
            created_at
          )
        `)
        .or(`name.ilike.%${searchQuery}%,primary_email.ilike.%${searchQuery}%`)
        .limit(20);

      if (error) throw error;

      const processedClients = (data || []).map(client => {
        const needsAnalyses = client.compliance_documents?.filter(
          (doc: any) => doc.document_type === 'needs_analysis'
        ) || [];
        
        const lastNA = needsAnalyses.sort((a: any, b: any) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        )[0];

        return {
          id: client.id,
          name: client.name,
          primary_email: client.primary_email,
          primary_phone: client.primary_phone,
          last_needs_analysis: lastNA ? {
            id: lastNA.id,
            created_at: lastNA.created_at,
            status: lastNA.status
          } : undefined
        };
      });

      setClients(processedClients);
    } catch (error) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Error searching clients:', error);
        }

      }
    }
  };

  const handleSelectClient = (client: Client | null) => {
    setSelectedClient(client);
    onSelectClient(client);
  };

  const getTimeAgo = (date: string) => {
    return formatDistanceToNow(new Date(date), { addSuffix: true });
  };

  const getStatusDisplay = (client: Client) => {
    if (!client.last_needs_analysis) {
      return {
        text: 'No previous analysis',
        subtext: 'Start fresh needs analysis',
        icon: null,
        color: 'text-gray-500'
      };
    }

    const timeAgo = getTimeAgo(client.last_needs_analysis.created_at);
    const monthsAgo = Math.floor(
      (Date.now() - new Date(client.last_needs_analysis.created_at).getTime()) / 
      (1000 * 60 * 60 * 24 * 30)
    );

    if (monthsAgo < 3) {
      return {
        text: `Last NA: ${timeAgo}`,
        subtext: '✓ Recently updated',
        icon: <CheckCircleIcon className="h-5 w-5 text-green-500" />,
        color: 'text-green-600'
      };
    } else {
      return {
        text: `Last NA: ${timeAgo}`,
        subtext: '⚡ Quick update available',
        icon: <ClockIcon className="h-5 w-5 text-amber-500" />,
        color: 'text-amber-600'
      };
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Select or Add Client</h2>

      {/* Search Input */}
      <div className="relative mb-6">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search existing clients..."
          className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500 text-gray-900 placeholder-gray-400 bg-white"
        />
      </div>

      {/* Client List */}
      <div className="space-y-2 mb-4">
        <h3 className="text-sm font-medium text-gray-700 mb-2">
          {searchQuery ? 'Search Results' : 'Recent Clients'}
        </h3>
        
        {loading ? (
          <div className="text-center py-4 text-gray-500">Loading clients...</div>
        ) : (
          <>
            {(searchQuery ? clients : recentClients).map((client) => {
              const status = getStatusDisplay(client);
              return (
                <label
                  key={client.id}
                  className={`block p-4 border rounded-lg cursor-pointer transition-all ${
                    selectedClient?.id === client.id
                      ? 'border-amber-500 bg-amber-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="client"
                    value={client.id}
                    checked={selectedClient?.id === client.id}
                    onChange={() => handleSelectClient(client)}
                    className="sr-only"
                  />
                  <div className="flex items-start justify-between">
                    <div className="flex-grow">
                      <div className="font-medium text-gray-900">{client.name}</div>
                      <div className="text-sm text-gray-500">{client.primary_email}</div>
                      {documentType === 'needs_analysis' && (
                        <div className={`text-sm mt-1 ${status.color}`}>
                          {status.text}
                          {status.subtext && <div className="text-xs">{status.subtext}</div>}
                        </div>
                      )}
                    </div>
                    {status.icon && documentType === 'needs_analysis' && (
                      <div className="ml-3">{status.icon}</div>
                    )}
                  </div>
                </label>
              );
            })}

            {/* New Client Option */}
            <label
              className={`block p-4 border rounded-lg cursor-pointer transition-all ${
                selectedClient === null && searchQuery === ''
                  ? 'border-amber-500 bg-amber-50'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <input
                type="radio"
                name="client"
                value="new"
                checked={selectedClient === null && searchQuery === ''}
                onChange={() => handleSelectClient(null)}
                className="sr-only"
              />
              <div className="flex items-center">
                <UserPlusIcon className="h-5 w-5 text-amber-500 mr-3" />
                <div>
                  <div className="font-medium text-gray-900">New Client</div>
                  <div className="text-sm text-gray-500">Start fresh needs analysis</div>
                </div>
              </div>
            </label>
          </>
        )}
      </div>

      {/* Info Box for Needs Analysis */}
      {documentType === 'needs_analysis' && selectedClient?.last_needs_analysis && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
          <p className="text-blue-800">
            <strong>Version Comparison Available:</strong> We'll show you the previous 
            information side-by-side with the current form, making updates quick and easy.
          </p>
        </div>
      )}
    </div>
  );
}